import Head from "next/head";
import { useRouter } from "next/router";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import ToResolveClassNameError from "../components/hoc/ToResolveClassNameError";
import HomepageWrapper from "../components/homepage/HomepageWrapper/HomepageWrapper";
import SharedFooter from "../components/shared/SharedFooter/SharedFooter";
import HPUploadResumeFloating from "../components/shared/UploadResumeFloating/wrappers/HPUploadResumeFloating";
import * as config from "../config/config";
import { updateShowSignInModalStatus } from "../redux/actions/navbar_action";
import { getUser } from "../redux/actions/user_action";

function Home(props) {
  const router = useRouter();
  const cUser = getUser();
  const { t } = useTranslation();

  if (router.query.reset_password) {
    if (cUser) {
      router.push("/");
    } else {
      props.updateShowSignInModalStatus(true);
    }
  }

  const isSingapore = process.env.NEXT_PUBLIC_JSW_GEOLOCATION === "sg";

  const siteOrigin = isSingapore
    ? "https://sg.hiredly.com"
    : "https://my.hiredly.com";

  const metaTitle = isSingapore
    ? "Find Jobs in Singapore | Hiredly"
    : t("homepage.meta_title");

  const metaDescription = isSingapore
    ? "Find full-time, remote, work-from-home, and internship career opportunities in Singapore on Hiredly."
    : t("homepage.meta_description");
  return (
    <Fragment>
      <Head>
        {/* <!-- Primary Meta Tags --> */}
        <title>{metaTitle}</title>
        <meta name="title" content={metaTitle} />
        <meta name="description" content={metaDescription} />

        {/* <!-- Open Graph / Facebook --> */}
        <meta property="og:type" content="website" key={"ogType"} />
        <meta property="og:url" content={`${siteOrigin}/`} key="ogUrl" />
        <meta property="og:title" content={metaTitle} key="ogTitle" />
        <meta
          property="og:description"
          content={metaDescription}
          key="ogDescription"
        />
        <meta
          property="og:image"
          content={
            config.assetDomain + "/images/hiredly/hiredly-meta-image-v1.png"
          }
          key="ogImage"
        />

        {/* <!-- Twitter --> */}
        <meta
          property="twitter:card"
          content="summary_large_image"
          key="twitterCard"
        />
        <meta
          property="twitter:url"
          content={`${siteOrigin}/`}
          key="twitterUrl"
        />
        <meta property="twitter:title" content={metaTitle} key="twitterTitle" />
        <meta
          property="twitter:description"
          content={metaDescription}
          key="twitterDescription"
        />
        <meta
          property="twitter:image"
          content={
            config.assetDomain + "/images/hiredly/hiredly-meta-image-v1.png"
          }
          key="twitterImage"
        />
        <meta
          name="viewport"
          content="minimum-scale=1, initial-scale=1, width=device-width"
        />
        <link rel="canonical" href={`${siteOrigin}/`} />

        {/* iOS Smart App Banner */}
        <meta
          name="apple-itunes-app"
          content={`app-id=981491669, app-argument=${siteOrigin}/`}
        />
      </Head>
      <ToResolveClassNameError>
        <HomepageWrapper />
        {props.showMobileSearch ? null : (
          <Fragment>
            <SharedFooter />
          </Fragment>
        )}
      </ToResolveClassNameError>
      <HPUploadResumeFloating />
    </Fragment>
  );
}

const mapStateToProps = (state) => {
  return {
    showMobileSearch: state.navbar.showMobileSearch,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    updateShowSignInModalStatus: bindActionCreators(
      updateShowSignInModalStatus,
      dispatch
    ),
  };
};

export const getServerSideProps = async (context) => {
  const { req, res, query } = context;
  const geoEnv = process.env.NEXT_PUBLIC_JSW_GEOLOCATION ?? "my";

  const userId = req.cookies?.userId;
  // const companyId = req.cookies?.companyId;
  const isLoggedIn = Boolean(userId);
  const hasBeenRedirected = req.cookies?.homepageRedirected === "true";

  // Do not redirect to /jobs when user just completed reset password (they should stay on "/" to see success message / login modal)
  const isResetPasswordReturn =
    query?.reset_password === "true" ||
    (query?.login === "true" && query?.migration !== undefined);

  // ✅ Logged-in users → redirect to JLP or FYP based on resume status (only on first visit)
  if (isLoggedIn && !hasBeenRedirected && !isResetPasswordReturn) {
    // Set cookie to mark that user has been redirected
    res.setHeader(
      "Set-Cookie",
      "homepageRedirected=true; Path=/; HttpOnly; Max-Age=86400"
    ); // 24 hours

    return {
      redirect: {
        destination: "/jobs",
        permanent: false,
      },
    };
  }

  // if user website geolocation is set to SG, redirect them to the /jobs page
  if (geoEnv === "sg") {
    return {
      redirect: {
        permanent: false,
        destination: "/jobs",
      },
    };
  }

  return { props: {} };
};

export default connect(mapStateToProps, mapDispatchToProps)(Home);
